import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import EmailsTab from "../components/EmailsTab";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Settings,
  Calendar,
  CheckSquare,
  Trash2,
  AlertTriangle,
  RefreshCw,
  MessageSquare,
  Users,
  Code,
  FileText,
  Key,
  Eye,
  EyeOff,
  Copy,
  AlertCircle,
  Building2,
  Mail,
} from "lucide-react";

export default function ConfiguracionPage() {
  const [loading, setLoading] = useState({});
  const [results, setResults] = useState({});
  const [activeTab, setActiveTab] = useState("general");
  const [showApiKey, setShowApiKey] = useState(false);
  const [empresaConfig, setEmpresaConfig] = useState({
    nombre_empresa: "",
    cif: "",
    direccion: "",
    telefono: "",
    email: "",
    logo_url_base64: ""
  });

  useEffect(() => {
    loadEmpresaConfig();
  }, []);

  const loadEmpresaConfig = async () => {
    try {
      const response = await api.getEmpresaConfig();
      if (response.data && Object.keys(response.data).length > 0) {
        setEmpresaConfig(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Error loading empresa config:", error);
    }
  };

  const handleSaveEmpresaConfig = async () => {
    setLoading(prev => ({ ...prev, saveEmpresa: true }));
    try {
      await api.updateEmpresaConfig(empresaConfig);
      toast.success("Configuración de empresa guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setLoading(prev => ({ ...prev, saveEmpresa: false }));
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) { // 1MB limit
        toast.error("El logo debe ser menor a 1MB");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEmpresaConfig(prev => ({ ...prev, logo_url_base64: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAction = async (action, actionKey) => {
    setLoading(prev => ({ ...prev, [actionKey]: true }));

    try {
      let response;
      switch (action) {
        case 'limpiarFechas':
          response = await api.limpiarFechasContacto();
          break;
        case 'limpiarTareas':
          response = await api.limpiarTareasPendientes();
          break;
        case 'limpiarCompletas':
          response = await api.limpiarCompletas();
          break;
        default:
          throw new Error('Acción no reconocida');
      }

      setResults(prev => ({ ...prev, [actionKey]: response.data }));
      toast.success(response.data.message);

      // Mostrar detalles adicionales si existen
      if (response.data.modified_count !== undefined) {
        toast.info(`Registros modificados: ${response.data.modified_count}`);
      }
      if (response.data.deleted_count !== undefined) {
        toast.info(`Tareas eliminadas: ${response.data.deleted_count}`);
      }

    } catch (error) {
      console.error(`Error en ${action}:`, error);
      const errorMessage = error.response?.data?.detail || error.message || "Error desconocido";
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setLoading(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const consultasActions = [
    {
      key: 'limpiarFechas',
      title: 'Limpiar Fechas de Próximo Contacto',
      description: 'Elimina la fecha de próximo contacto de todas las consultas convertidas o descartadas',
      icon: Calendar,
      variant: 'outline',
      result: results.limpiarFechas
    },
    {
      key: 'limpiarTareas',
      title: 'Limpiar Tareas Pendientes',
      description: 'Elimina todas las tareas pendientes de consultas convertidas o descartadas',
      icon: CheckSquare,
      variant: 'outline',
      result: results.limpiarTareas
    },
    {
      key: 'limpiarCompletas',
      title: 'Limpieza Completa',
      description: 'Ejecuta ambas acciones: limpiar fechas de contacto y tareas pendientes',
      icon: RefreshCw,
      variant: 'default',
      result: results.limpiarCompletas
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <h1 className="text-3xl font-bold text-primary flex items-center justify-center gap-3">
            <Settings className="w-8 h-8" />
            Configuración
          </h1>
          <p className="text-muted-foreground">
            Configuración general y mantenimiento del sistema
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 h-auto p-1 bg-muted/50">
            <TabsTrigger value="general" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">General</span>
            </TabsTrigger>
            <TabsTrigger value="emails" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Emails</span>
            </TabsTrigger>
            <TabsTrigger value="consultas" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Consultas</span>
            </TabsTrigger>
            <TabsTrigger value="clientes" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">Clientes</span>
            </TabsTrigger>
            <TabsTrigger value="calendario" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Calendario</span>
            </TabsTrigger>
            <TabsTrigger value="api" className="flex items-center gap-2 h-12 data-[state=active]:bg-background data-[state=active]:shadow-sm">
              <Code className="w-4 h-4" />
              <span className="hidden sm:inline">API</span>
            </TabsTrigger>
          </TabsList>

          {/* Emails */}
          <TabsContent value="emails" className="mt-6">
            <EmailsTab />
          </TabsContent>

          {/* General */}
          <TabsContent value="general" className="mt-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Configuración General
                </h3>
                <div className="space-y-6 max-w-2xl mt-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Nombre de la Empresa / Fiscal</label>
                      <Input
                        value={empresaConfig.nombre_empresa}
                        onChange={(e) => setEmpresaConfig({ ...empresaConfig, nombre_empresa: e.target.value })}
                        placeholder="Ej. Fotomatón Costa Brava S.L."
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">CIF / NIF</label>
                      <Input
                        value={empresaConfig.cif}
                        onChange={(e) => setEmpresaConfig({ ...empresaConfig, cif: e.target.value })}
                        placeholder="Ej. B12345678"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">Dirección</label>
                    <Input
                      value={empresaConfig.direccion}
                      onChange={(e) => setEmpresaConfig({ ...empresaConfig, direccion: e.target.value })}
                      placeholder="Ej. Calle Principal 123, 17001 Girona"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Teléfono</label>
                      <Input
                        value={empresaConfig.telefono}
                        onChange={(e) => setEmpresaConfig({ ...empresaConfig, telefono: e.target.value })}
                        placeholder="Ej. +34 600 000 000"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Email de Facturación</label>
                      <Input
                        value={empresaConfig.email}
                        onChange={(e) => setEmpresaConfig({ ...empresaConfig, email: e.target.value })}
                        placeholder="Ej. facturacion@empresa.com"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <label className="text-sm font-medium block">Logo de la Empresa (para PDFs)</label>
                    <div className="flex items-center gap-6">
                      <div className="h-24 w-24 rounded-lg border-2 border-dashed border-border flex items-center justify-center bg-muted overflow-hidden shrink-0">
                        {empresaConfig.logo_url_base64 ? (
                          <img src={empresaConfig.logo_url_base64} alt="Logo" className="max-h-full max-w-full object-contain" />
                        ) : (
                          <Building2 className="h-8 w-8 text-muted-foreground opacity-50" />
                        )}
                      </div>
                      <div className="space-y-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="max-w-[250px] cursor-pointer"
                        />
                        <p className="text-xs text-muted-foreground">Recomendado: imagen cuadrada o rectangular horizontal, formato PNG/JPG, máx 1MB.</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end border-t border-border/40">
                    <Button onClick={handleSaveEmpresaConfig} disabled={loading.saveEmpresa}>
                      {loading.saveEmpresa ? "Guardando..." : "Guardar Configuración"}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Consultas */}
          <TabsContent value="consultas" className="mt-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Mantenimiento de Consultas
                </h3>

                {/* Alerta de seguridad */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <p className="font-medium text-amber-800">Atención</p>
                      <p className="text-sm text-amber-700">
                        Estas acciones modifican datos de forma permanente.
                        Asegúrate de entender qué hace cada botón antes de ejecutarlo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {consultasActions.map((action) => {
                    const Icon = action.icon;
                    const isLoading = loading[action.key];
                    const result = action.result;

                    return (
                      <Card key={action.key} className="border-0 shadow-soft hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Icon className="w-5 h-5" />
                            {action.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {action.description}
                          </p>

                          {/* Resultados anteriores */}
                          {result && (
                            <div className="space-y-2 p-3 bg-muted/30 rounded-lg">
                              <p className="text-xs font-medium text-muted-foreground">Última ejecución:</p>
                              <div className="space-y-1">
                                <Badge variant="secondary" className="text-xs">
                                  {result.message}
                                </Badge>
                                {result.modified_count !== undefined && (
                                  <p className="text-xs text-muted-foreground">
                                    Registros modificados: <strong>{result.modified_count}</strong>
                                  </p>
                                )}
                                {result.deleted_count !== undefined && (
                                  <p className="text-xs text-muted-foreground">
                                    Tareas eliminadas: <strong>{result.deleted_count}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                          )}

                          <Button
                            onClick={() => handleAction(action.key, action.key)}
                            variant={action.variant}
                            className="w-full"
                            disabled={isLoading}
                          >
                            {isLoading ? (
                              <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Ejecutando...
                              </>
                            ) : (
                              <>
                                <Icon className="w-4 h-4 mr-2" />
                                Ejecutar Acción
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Información adicional */}
                <div className="mt-6 p-4 bg-muted/30 rounded-lg">
                  <h4 className="font-medium mb-3">Información de Mantenimiento</h4>
                  <div className="grid md:grid-cols-2 gap-6 text-sm text-muted-foreground">
                    <div className="space-y-2">
                      <p><strong>Limpiar Fechas:</strong> Elimina <code>fecha_proximo_contacto</code> de consultas finalizadas</p>
                      <p><strong>Limpiar Tareas:</strong> Elimina tareas pendientes de consultas finalizadas</p>
                      <p><strong>Limpieza Completa:</strong> Ejecuta ambas acciones simultáneamente</p>
                    </div>
                    <div className="space-y-2">
                      <p><strong>Estados afectados:</strong> Convertida, Descartada</p>
                      <p><strong>Frecuencia:</strong> Recomendado mensual o trimestral</p>
                      <p><strong>Impacto:</strong> Solo afecta datos antiguos, no modifica consultas activas</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clientes */}
          <TabsContent value="clientes" className="mt-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Configuración de Clientes
                </h3>
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No hay configuración disponible</p>
                  <p className="text-sm mt-2">Las opciones de clientes aparecerán aquí próximamente</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendario */}
          <TabsContent value="calendario" className="mt-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Configuración de Calendario
                </h3>
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No hay configuración disponible</p>
                  <p className="text-sm mt-2">Las opciones de calendario aparecerán aquí próximamente</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API */}
          <TabsContent value="api" className="mt-6">
            <Card className="border-0 shadow-soft">
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Configuración de API
                </h3>

                <div className="space-y-6">
                  {/* API Key Section */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-3 flex items-center gap-2">
                      <Key className="w-4 h-4" />
                      Clave de API
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Input
                          type={showApiKey ? "text" : "password"}
                          value="fcb-n8n-api-key-2024"
                          readOnly
                          className="font-mono text-sm bg-white"
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setShowApiKey(!showApiKey)}
                        >
                          {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            navigator.clipboard.writeText("fcb-n8n-api-key-2024");
                            toast.success("API Key copiada");
                          }}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-sm text-blue-700">
                        Usa esta clave en el header <code>X-API-Key</code> para autenticar las peticiones.
                      </p>
                    </div>
                  </div>

                  {/* Endpoints */}
                  <div className="space-y-4">
                    <h4 className="font-medium">Endpoints Disponibles</h4>

                    {/* Endpoint 1: Crear Consulta */}
                    <Card className="border border-border/60">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Crear Consulta</CardTitle>
                          <Badge variant="secondary">POST</Badge>
                        </div>
                        <CardDescription>
                          Crea una nueva consulta en el sistema. Ideal para automatizaciones con n8n o Zapier.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm font-medium mb-2">URL:</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-muted px-2 py-1 rounded flex-1">
                              {process.env.REACT_APP_BACKEND_URL}/api/public/consultas
                            </code>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/public/consultas`);
                                toast.success("URL copiada");
                              }}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Headers requeridos:</p>
                          <code className="text-sm bg-muted px-2 py-1 rounded block">
                            Content-Type: application/json<br />
                            X-API-Key: fcb-n8n-api-key-2024
                          </code>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Body (JSON):</p>
                          <div className="space-y-1 text-sm">
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>nombre</strong> <span className="text-red-500">*</span></span>
                              <span className="text-muted-foreground">string</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>telefono</strong> <span className="text-red-500">*</span></span>
                              <span className="text-muted-foreground">string</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>email</strong> <span className="text-red-500">*</span></span>
                              <span className="text-muted-foreground">email válido</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>fecha_evento</strong></span>
                              <span className="text-muted-foreground">YYYY-MM-DD (opcional)</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>tipo_evento</strong></span>
                              <span className="text-muted-foreground">string (opcional)</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>ubicacion</strong></span>
                              <span className="text-muted-foreground">string (opcional)</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>notas</strong></span>
                              <span className="text-muted-foreground">string (opcional)</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>fuente</strong></span>
                              <span className="text-muted-foreground">string (opcional)</span>
                            </div>
                            <div className="flex justify-between p-2 bg-muted rounded">
                              <span><strong>fecha_proximo_contacto</strong></span>
                              <span className="text-muted-foreground">ISO datetime (opcional)</span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Ejemplo:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                            {`curl -X POST "${process.env.REACT_APP_BACKEND_URL}/api/public/consultas" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: fcb-n8n-api-key-2024" \\
  -d '{
    "nombre": "Juan Pérez",
    "telefono": "600123456",
    "email": "juan@email.com",
    "fecha_evento": "2024-12-25",
    "tipo_evento": "Boda",
    "ubicacion": "Salón Barcelona",
    "fuente": "web"
  }'`}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Endpoint 2: Obtener Consultas */}
                    <Card className="border border-border/60">
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-base">Obtener Todas las Consultas</CardTitle>
                          <Badge variant="secondary">GET</Badge>
                        </div>
                        <CardDescription>
                          Obtiene todas las consultas del sistema sin importar el estado.
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <p className="text-sm font-medium mb-2">URL:</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-muted px-2 py-1 rounded flex-1">
                              {process.env.REACT_APP_BACKEND_URL}/api/public/consultas
                            </code>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                navigator.clipboard.writeText(`${process.env.REACT_APP_BACKEND_URL}/api/public/consultas`);
                                toast.success("URL copiada");
                              }}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Headers requeridos:</p>
                          <code className="text-sm bg-muted px-2 py-1 rounded block">
                            X-API-Key: fcb-n8n-api-key-2024
                          </code>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-2">Ejemplo:</p>
                          <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                            {`curl -X GET "${process.env.REACT_APP_BACKEND_URL}/api/public/consultas" \\
  -H "X-API-Key: fcb-n8n-api-key-2024"`}
                          </pre>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Info */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="space-y-1">
                        <p className="font-medium text-amber-800">Información Importante</p>
                        <p className="text-sm text-amber-700">
                          La API es pública pero requiere autenticación mediante API Key.
                          Todos los endpoints retornan datos en formato JSON y siguen estándares REST.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
